import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import leadPurchasesService from '../../services/leadPurchases';
import { Card, Button, Typography, Row, Col,  message, Divider, Space, Tag,  Checkbox, Input, Modal, Skeleton } from 'antd';
import { DownloadOutlined, ArrowLeftOutlined, UserOutlined, PhoneOutlined, MailOutlined, EnvironmentOutlined, CalendarOutlined, DollarOutlined, HeartOutlined, HomeOutlined, ClockCircleOutlined, SettingOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

// Reusable hook for column/section visibility
function useColumnVisibility(key, allColumns) {
  const storageKey = `column-visibility-${key}`;
  const [visibleKeys, setVisibleKeys] = React.useState(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored) return JSON.parse(stored);
    return allColumns.map(col => col.key);
  });

  React.useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(visibleKeys));
  }, [visibleKeys, storageKey]);

  const setColumnVisibility = (key, visible) => {
    setVisibleKeys(prev => {
      if (visible) return Array.from(new Set([...prev, key]));
      return prev.filter(k => k !== key);
    });
  };

  const resetColumns = () => {
    setVisibleKeys(allColumns.map(col => col.key));
  };

  return {
    visibleKeys,
    setColumnVisibility,
    resetColumns,
    isVisible: (key) => visibleKeys.includes(key)
  };
}

const PurchaseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: purchase, isLoading, error } = useQuery({
    queryKey: ['purchase', id],
    queryFn: () => leadPurchasesService.getPurchase(id),
    onError: (error) => {
      if (error.response?.status === 403 || error.response?.status === 404) {
        navigate('/purchases');
      }
    },
    retry: false
  });

  // Add error handling for unauthorized access and not found
  React.useEffect(() => {
    if (error?.response?.status === 403 || error?.response?.status === 404) {
      navigate('/purchases');
    }
  }, [error, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return dayjs(dateString).format('MMM D, YYYY');
  };

  const handleDownloadExcel = () => {
    if (!purchase || !purchase.lead) return;

    const leadData = [
      {
        'First Name': purchase.lead.first_name,
        'Last Name': purchase.lead.last_name,
        'Phone': purchase.lead.phone,
        'Email': purchase.lead.email,
        'Lead Old': formatDate(purchase.lead.lead_old),
        'Last Activity': formatDate(purchase.lead.last_activity),
        'State': purchase.lead.state_name,
        'Birth Date': formatDate(purchase.lead.birth_date),
        'Type of Life Insurance': purchase.lead.type_of_life_insurance,
        'Coverage Amount': purchase.lead.coverage_amount,
        'Final Expense Coverage': purchase.lead.final_expense_coverage,
        'Beneficiary': purchase.lead.beneficiary,
        'Are you looking for additional retirement income sources?': purchase.lead.additional_retirement_income,
        'Annual Income': purchase.lead.annual_income,
        'Do you have interest in an annuity?': purchase.lead.guaranteed_account_interest,
        // 'Can We Contact': purchase.lead.can_we_contact,
        'Mortgage Protection Amount': purchase.lead.mortgage_protection_amount,
        // 'Funeral Type': purchase.lead.funeral_type,
        // 'Similar Plan Contact Permission': purchase.lead.similar_plan_contact_permission,
        // 'Mortgage Amount Owed': purchase.lead.mortgage_amount_owed,
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(leadData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Lead Details');
    XLSX.writeFile(workbook, `lead-${id}.xlsx`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Yes':
        return 'success';
      case 'No':
        return 'error';
      default:
        return 'default';
    }
  };

  const allDetailSections = [
    { title: 'Personal Information', key: 'personal' },
    { title: 'Insurance Details', key: 'insurance' },
    { title: 'Financial Information', key: 'financial' },
    { title: 'Mortgage Information', key: 'mortgage' },
    { title: 'Additional Information', key: 'additional' },
  ];

  const {
    visibleKeys: visibleSectionKeys,
    setColumnVisibility,
    resetColumns,
    isVisible
  } = useColumnVisibility('purchase-detail-sections', allDetailSections);
  const [customizeModalOpen, setCustomizeModalOpen] = React.useState(false);
  const [sectionSearch, setSectionSearch] = React.useState('');
  const filteredSectionOptions = allDetailSections.filter(col => {
    if (!sectionSearch) return true;
    return (col.title || '').toLowerCase().includes(sectionSearch.toLowerCase());
  });

  return (
    <div style={{ 
      padding: '12px',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
      minHeight: '100vh'
    }}>
      <div className="page-header" style={{
        marginBottom: '24px',
        padding: '16px',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle at top right, rgba(24,144,255,0.1) 0%, rgba(24,144,255,0) 70%)',
          pointerEvents: 'none'
        }} />
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          position: 'relative',
          zIndex: 1
        }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/purchases')}
            style={{ 
              marginRight: 'auto',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            Back to Purchases
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Card 
          style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            overflow: 'hidden'
          }}
        >
          {/* Header Section Skeleton */}
          <div style={{ 
            background: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
            padding: '24px', 
            color: 'white',
            marginBottom: 16,
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '16px'
          }}>
            <Row gutter={[24, 24]} justify="space-between" align="middle">
              <Col xs={24} sm={24} md={16}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                  <Skeleton.Avatar active size={64} shape="square" style={{ borderRadius: '16px' }} />
                  <div style={{ flex: 1 }}>
                    <Skeleton.Input active size="large" style={{ width: 200, marginBottom: 8 }} />
                    <Skeleton.Input active size="small" style={{ width: 150 }} />
                  </div>
                </div>
                <Space size={[16, 16]} wrap style={{ marginTop: 16 }}>
                  <Skeleton.Button active size="large" style={{ width: 200, height: 48, borderRadius: '12px' }} />
                  <Skeleton.Button active size="large" style={{ width: 200, height: 48, borderRadius: '12px' }} />
                  <Skeleton.Button active size="large" style={{ width: 200, height: 48, borderRadius: '12px' }} />
                </Space>
              </Col>
              <Col xs={24} sm={24} md={8} style={{ textAlign: { xs: 'left', sm: 'left', md: 'right' } }}>
                <Space>
                  <Skeleton.Button active size="large" style={{ width: 160, height: 48, borderRadius: '12px' }} />
                  <Skeleton.Button active size="large" style={{ width: 160, height: 48, borderRadius: '12px' }} />
                </Space>
              </Col>
            </Row>
          </div>

          {/* Quick Stats Skeleton */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {[1, 2, 3].map((_, index) => (
              <Col xs={24} sm={12} md={8} key={index}>
                <Card 
                  bordered={false} 
                  style={{ 
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Skeleton.Avatar active size={48} shape="square" style={{ borderRadius: '12px' }} />
                    <div style={{ flex: 1 }}>
                      <Skeleton.Input active size="small" style={{ width: 100, marginBottom: 8 }} />
                      <Skeleton.Input active size="large" style={{ width: 150 }} />
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
          {/* Customize Sections Button Skeleton */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Skeleton.Button active size="large" style={{ width: 180, height: 40, borderRadius: '8px' }} />
          </div>

          {/* Main Content Skeleton */}
          <Row gutter={[16, 16]}>
            {[1, 2, 3, 4, 5].map((_, index) => (
              <Col xs={24} md={12} key={index}>
                <Card 
                  bordered={false} 
                  style={{ 
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: 16 }}>
                    <Skeleton.Avatar active size={32} shape="square" style={{ borderRadius: '8px' }} />
                    <Skeleton.Input active size="large" style={{ width: 200 }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {[1, 2, 3].map((_, itemIndex) => (
                      <div key={itemIndex}>
                        <Skeleton.Input active size="small" style={{ width: 150, marginBottom: 8 }} />
                        <Skeleton.Input active size="large" style={{ width: '100%' }} />
                      </div>
                    ))}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      ) : (
        <Card 
          style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            overflow: 'hidden'
          }}
        >
          {/* Header Section */}
          <div style={{ 
            background: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
            padding: '24px', 
            color: 'white',
            marginBottom: 16,
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '16px'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '400px',
              height: '400px',
              background: 'radial-gradient(circle at top right, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 70%)',
              pointerEvents: 'none'
            }} />
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '300px',
              height: '300px',
              background: 'radial-gradient(circle at bottom left, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 70%)',
              pointerEvents: 'none'
            }} />
            <Row gutter={[24, 24]} justify="space-between" align="middle">
              <Col xs={24} sm={24} md={16}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(10px)'
                  }}>
                    <UserOutlined style={{ fontSize: '32px', color: '#fff' }} />
                  </div>
                  <div>
                    <Typography.Title level={2} style={{ 
                      margin: 0, 
                      color: 'white', 
                      fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                      fontWeight: 600,
                      textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      {purchase?.lead?.first_name} {purchase?.lead?.last_name}
                    </Typography.Title>
                  
                  </div>
                </div>
                <Space size={[16, 16]} wrap style={{ marginTop: 16 }}>
                  <div style={{ 
                    background: 'rgba(255,255,255,0.15)', 
                    padding: '12px 20px', 
                    borderRadius: '12px',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <PhoneOutlined style={{ fontSize: '20px' }} />
                    <span style={{ fontSize: '16px' }}>{purchase?.lead?.phone}</span>
                  </div>
                  <div style={{ 
                    background: 'rgba(255,255,255,0.15)', 
                    padding: '12px 20px', 
                    borderRadius: '12px',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <MailOutlined style={{ fontSize: '20px' }} />
                    <span style={{ fontSize: '16px' }}>{purchase?.lead?.email}</span>
                  </div>
                  <div style={{ 
                    background: 'rgba(255,255,255,0.15)', 
                    padding: '12px 20px', 
                    borderRadius: '12px',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <EnvironmentOutlined style={{ fontSize: '20px' }} />
                    <span style={{ fontSize: '16px' }}>{purchase?.lead?.state_name}</span>
                  </div>
                </Space>
              </Col>
              <Col xs={24} sm={24} md={8} style={{ textAlign: { xs: 'left', sm: 'left', md: 'right' } }}>
                <Space>
                  <Button 
                    type="primary" 
                    size='large'
                    icon={<DownloadOutlined />} 
                    onClick={handleDownloadExcel}
                    style={{
                      background: 'rgba(255,255,255,0.15)',
                      color: '#fff',
                      border: 'none',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      width: { xs: '100%', sm: 'auto' },
                      borderRadius: '12px',
                      height: '48px',
                      fontSize: '16px',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '0 24px'
                    }}
                  >
                    Download Excel
                  </Button>
                  <Button 
                    type="primary" 
                    size='large'
                    onClick={() => navigate(`/purchases/${id}/invoice`)}
                    style={{
                      background: 'rgba(255,255,255,0.15)',
                      color: '#fff',
                      border: 'none',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      width: { xs: '100%', sm: 'auto' },
                      borderRadius: '12px',
                      height: '48px',
                      fontSize: '16px',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '0 24px'
                    }}
                  >
                    View Invoice
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>

          {/* Quick Stats */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={8}>
              <Card 
                bordered={false} 
                style={{ 
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CalendarOutlined style={{ fontSize: '24px', color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#666', fontWeight: 500 }}>Lead Date</div>
                    <div style={{ fontSize: '20px', fontWeight: 600, color: '#1890ff' }}>
                      {formatDate(purchase?.lead?.lead_old)}
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card 
                bordered={false} 
                style={{ 
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <DollarOutlined style={{ fontSize: '24px', color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#666', fontWeight: 500 }}>Coverage Amount</div>
                    <div style={{ fontSize: '20px', fontWeight: 600, color: '#52c41a' }}>
                      {purchase?.lead?.coverage_amount}
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card 
                bordered={false} 
                style={{ 
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <DollarOutlined style={{ fontSize: '24px', color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#666', fontWeight: 500 }}>Annual Income</div>
                    <div style={{ fontSize: '20px', fontWeight: 600, color: '#722ed1' }}>
                      {purchase?.lead?.annual_income}
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Customize Sections */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Button 
              icon={<SettingOutlined />} 
              onClick={() => setCustomizeModalOpen(true)}
              style={{ borderRadius: '8px' }}
            >
              Customize Sections
            </Button>
          </div>

          {/* Main Content */}
          <Row gutter={[16, 16]}>
            {isVisible('personal') && (
              <Col xs={24} md={12}>
                <Card 
                  title={
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <UserOutlined style={{ fontSize: '16px', color: '#fff' }} />
                      </div>
                      <span>Personal Information</span>
                    </div>
                  }
                  bordered={false}
                  style={{ 
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {purchase?.lead?.birth_date && (
                      <div>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Birth Date</div>
                        <div style={{ fontSize: '16px' }}>
                          <Space><CalendarOutlined /> {formatDate(purchase?.lead?.birth_date)}</Space>
                        </div>
                      </div>
                    )}
                    {purchase?.lead?.lead_old && (
                      <div>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Lead Date</div>
                        <div style={{ fontSize: '16px' }}>
                          <Space><CalendarOutlined /> {formatDate(purchase?.lead?.lead_old)}</Space>
                        </div>
                      </div>
                    )}
                    {purchase?.lead?.last_activity && (
                      <div>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Last Activity</div>
                        <div style={{ fontSize: '16px' }}>
                          <Space><ClockCircleOutlined /> {formatDate(purchase?.lead?.last_activity)}</Space>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
            )}

            {isVisible('insurance') && (
              <Col xs={24} md={12}>
                <Card 
                  title={
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <HeartOutlined style={{ fontSize: '16px', color: '#fff' }} />
                      </div>
                      <span>Insurance Details</span>
                    </div>
                  }
                  bordered={false}
                  style={{ 
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {purchase?.lead?.type_of_life_insurance && (
                      <div>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Type of Life Insurance</div>
                        <div style={{ fontSize: '16px' }}>
                          <Space><HeartOutlined /> {purchase?.lead?.type_of_life_insurance}</Space>
                        </div>
                      </div>
                    )}
                    {purchase?.lead?.coverage_amount && (
                      <div>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Coverage Amount</div>
                        <div style={{ fontSize: '16px' }}>
                          <Space><DollarOutlined /> {purchase?.lead?.coverage_amount}</Space>
                        </div>
                      </div>
                    )}
                    {purchase?.lead?.final_expense_coverage && (
                      <div>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Final Expense Coverage</div>
                        <div style={{ fontSize: '16px' }}>
                          <Space><DollarOutlined /> {purchase?.lead?.final_expense_coverage}</Space>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
            )}

            {isVisible('financial') && (
              <Col xs={24} md={12}>
                <Card 
                  title={
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <DollarOutlined style={{ fontSize: '16px', color: '#fff' }} />
                      </div>
                      <span>Financial Information</span>
                    </div>
                  }
                  bordered={false}
                  style={{ 
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {purchase?.lead?.annual_income && (
                      <div>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Annual Income</div>
                        <div style={{ fontSize: '16px' }}>
                          <Space><DollarOutlined /> {purchase?.lead?.annual_income}</Space>
                        </div>
                      </div>
                    )}
                    {purchase?.lead?.additional_retirement_income && (
                      <div>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Are you looking for additional retirement income sources?
                        </div>
                        <div style={{ fontSize: '16px' }}>
                          <Space><DollarOutlined /> {purchase?.lead?.additional_retirement_income}</Space>
                        </div>
                      </div>
                    )}
                    {purchase?.lead?.guaranteed_account_interest && (
                      <div>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Do you have interest in an annuity?</div>
                        <div style={{ fontSize: '16px' }}>
                          <Space><DollarOutlined /> {purchase?.lead?.guaranteed_account_interest}</Space>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
            )}

            {isVisible('mortgage') && (
              <Col xs={24} md={12}>
                <Card 
                  title={
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        background: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <HomeOutlined style={{ fontSize: '16px', color: '#fff' }} />
                      </div>
                      <span>Mortgage Information</span>
                    </div>
                  }
                  bordered={false}
                  style={{ 
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {purchase?.lead?.mortgage_protection_amount && (
                      <div>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Mortgage Protection Amount</div>
                        <div style={{ fontSize: '16px' }}>
                          <Space><HomeOutlined /> {purchase?.lead?.mortgage_protection_amount}</Space>
                        </div>
                      </div>
                    )}
                    {/* {purchase?.lead?.mortgage_amount_owed && (
                      <div>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Mortgage Amount Owed</div>
                        <div style={{ fontSize: '16px' }}>
                          <Space><HomeOutlined /> {purchase?.lead?.mortgage_amount_owed}</Space>
                        </div>
                      </div>
                    )} */}
                  </div>
                </Card>
              </Col>
            )}

            {isVisible('additional') && (
              <Col xs={24} md={12}>
                <Card 
                  title={
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        background: 'linear-gradient(135deg, #13c2c2 0%, #08979c 100%)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <SettingOutlined style={{ fontSize: '16px', color: '#fff' }} />
                      </div>
                      <span>Additional Information</span>
                    </div>
                  }
                  bordered={false}
                  style={{ 
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {purchase?.lead?.beneficiary && (
                      <div>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Beneficiary</div>
                        <div style={{ fontSize: '16px' }}>{purchase?.lead?.beneficiary}</div>
                      </div>
                    )}
                    {/* {purchase?.lead?.funeral_type && (
                      <div>
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Funeral Type</div>
                        <div style={{ fontSize: '16px' }}>{purchase?.lead?.funeral_type}</div>
                      </div>
                    )} */}
                    <div>
                      {/* <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Can We Contact</div>
                      <div style={{ fontSize: '16px' }}>
                        {purchase?.lead?.can_we_contact === true ? (
                          <Tag color={getStatusColor('Yes')}>
                            Yes
                          </Tag>
                        ) : (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>
                        )}
                      </div> */}
                    </div>
                    <div>
                      {/* <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Similar Plan Contact Permission</div> */}
                      <div style={{ fontSize: '16px' }}>
                        {purchase?.lead?.similar_plan_contact_permission === true ? (
                          <Tag color={getStatusColor('Yes')}>
                            Yes
                          </Tag>
                        ) : (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            )}
          </Row>
        </Card>
      )}

      <Modal
        title="Customize Sections"
        open={customizeModalOpen}
        onCancel={() => setCustomizeModalOpen(false)}
        footer={null}
        bodyStyle={{ padding: 24 }}
        width={600}
        style={{ borderRadius: '12px' }}
      >
        <div style={{ marginBottom: 12 }}>
          <Input
            placeholder="Search sections..."
            value={sectionSearch}
            onChange={e => setSectionSearch(e.target.value)}
            allowClear
            size="large"
            style={{ borderRadius: '8px' }}
          />
        </div>
        <Divider style={{ margin: '12px 0' }} />
        <div style={{
          maxHeight: 320,
          overflowY: 'auto',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 8,
          marginBottom: 16,
        }}>
          {filteredSectionOptions.map(col => (
            <Checkbox
              key={col.key}
              checked={isVisible(col.key)}
              onChange={e => setColumnVisibility(col.key, e.target.checked)}
              style={{ fontWeight: 400 }}
            >
              {col.title}
            </Checkbox>
          ))}
        </div>
        <Divider style={{ margin: '12px 0' }} />
        <Button 
          type="default" 
          block 
          onClick={resetColumns} 
          style={{ 
            fontWeight: 400,
            borderRadius: '8px',
            height: '40px'
          }}
        >
          Reset to Default
        </Button>
      </Modal>
    </div>
  );
};

export default PurchaseDetailPage;